/**
 * Hub message → the POS queries it makes stale (T07-40). The hub's `pos:register:<id>` topic
 * (services/realtime/app/mapping/*.yaml) says *what* changed; this table says which screens of the
 * register show it. The POS keys follow the `[root, "list" | "detail", …]` convention of
 * `@tcg/realtime/query` with `pos-` roots, so the library's default roots (`shifts`, `orders` …)
 * would miss them — hence an explicit map (an entity not listed refetches nothing).
 *
 * | entity (hub) | from | refetched on the register |
 * |---|---|---|
 * | `pos_sale` | `pos.sale.completed`, audit of a sale | 取引履歴, the sale, the drawer (open shift), 免税 records, 受取 list |
 * | `shift` | `pos.shift.closed`, audit of shift / 入出金 | the drawer + shift history, the register (open shift) |
 * | `payment` · `store_credit` | payment-service (register id) | the drawer (expected cash), 取引履歴 |
 * | `order` | `order.paid` of a POS order | 取引履歴 (pickup orders of every shop — `order.status.changed` / `cancelled`, T14-15 — are the 受取予定 feed's: `realtime/pickups.ts` filters them by this shop) |
 * | `pos_setting` | audit of `PATCH /admin/settings/pos` (T14-15) | the register (its rules: auto-lock, limits, offline policy …) |
 * | `hold` | `inventory.hold.expired` (owner = this register) | 保留 / 取り置き list + count, the basket |
 * | `taxfree_record` | `tax.taxfree.record_created` | 免税 records of the shift (the sale's own status listens itself) |
 * | `variant` | `inventory.availability.changed` (every register) | search results, quick buttons |
 * | `product` | catalog upsert / delete (every register) | search results, quick buttons, the basket (price changed) |
 * | `pos_device` | identity: tablet paired / revoked | the register |
 * | `buylist_intake` | `pos.buylist.accepted` | 買取 history |
 */
import type { ChangeMessage } from "@tcg/realtime";
import type { KeyMap, QueryKey } from "@tcg/realtime/query";

const list = (root: string): QueryKey => [root, "list"];
const all = (root: string): QueryKey => [root];

export const REGISTER_KEY: QueryKey = ["registers", "detail", "current"];

const DRAWER: QueryKey[] = [all("pos-shifts")];

const RULES: Readonly<Record<string, (message: ChangeMessage) => QueryKey[]>> = {
  pos_sale: (m) => [
    list("pos-sales"),
    ["pos-sales", "detail", m.id],
    ...DRAWER,
    list("pos-tax-free-records"),
    list("pos-pickups"),
  ],
  shift: () => [...DRAWER, REGISTER_KEY],
  payment: () => [...DRAWER, list("pos-sales")],
  store_credit: () => [...DRAWER],
  order: (m) => (m.action === "paid" ? [list("pos-sales")] : []),
  hold: () => [all("pos-holds"), all("pos-carts")],
  taxfree_record: () => [list("pos-tax-free-records")],
  variant: () => [list("pos-search"), list("pos-quick-items")],
  product: () => [list("pos-search"), list("pos-quick-items"), all("pos-carts")],
  pos_device: () => [REGISTER_KEY],
  buylist_intake: () => [all("pos-buylist")],
  pos_setting: () => [REGISTER_KEY],
};

/** Every key a hub message may touch — what refetches after a `resync` / the tab coming back. */
export const REGISTER_RESYNC_KEYS: readonly QueryKey[] = [
  list("pos-sales"),
  ...DRAWER,
  REGISTER_KEY,
  all("pos-holds"),
  list("pos-tax-free-records"),
  list("pos-pickups"),
  list("pos-search"),
  list("pos-quick-items"),
  all("pos-buylist"),
];

/** The key map of the POS (`<RealtimeProvider keyMap>`). */
export const posKeyMap: KeyMap = (message) => {
  const rule = message.entity ? RULES[message.entity] : undefined;
  return rule ? rule(message) : [];
};

/** Entities the map knows (tests pin it against the hub mapping). */
export const POS_KEYED_ENTITIES: readonly string[] = Object.keys(RULES);
